import React, { useEffect, useMemo, useState } from 'react';
import { dietPlansApi, dietProtocolsApi, patientsApi } from '../services/api';

const formatValue = (value, suffix, digits = 0) => value != null
  ? `${Number(value).toFixed(digits)}${suffix}`
  : 'Non disponibile';

export const makeDietBrief = patient => ({
  clinicalGoals: patient?.clinicalGoals || '',
  foodPreferences: patient?.foodPreferences || '',
  eatingHabits: patient?.eatingHabits || ''
});

export const isDietBriefComplete = brief => Object.values(brief).every(value => String(value || '').trim().length > 0);

export const getMissingCoreContext = (latestVisit, latestMeasurement) => [
  !latestVisit && 'ultima visita',
  !latestMeasurement && 'ultima BIA'
].filter(Boolean);

export const getDraftTransferStatus = transferResult => transferResult === false ? 'not-replaced' : 'opened';

export function AiDietPlanAssistant({ patient, latestMeasurement, latestVisit, onDraftReady, onPatientContextSaved, autoOpen = false, initialInstructions = '' }) {
  const [open, setOpen] = useState(Boolean(autoOpen));
  const [instructions, setInstructions] = useState(initialInstructions || '');
  const [planningMethod, setPlanningMethod] = useState('PortionsAndFrequencies');
  const [protocols, setProtocols] = useState([]);
  const [protocolsLoading, setProtocolsLoading] = useState(false);
  const [protocolError, setProtocolError] = useState('');
  const [protocolVersionId, setProtocolVersionId] = useState('');
  const [clinicianConfirmedEligibility, setClinicianConfirmedEligibility] = useState(false);
  const [targetCalories, setTargetCalories] = useState('');
  const [mealsPerDay, setMealsPerDay] = useState(5);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [brief, setBrief] = useState(() => makeDietBrief(patient));
  const [saveBriefToPatient, setSaveBriefToPatient] = useState(false);
  const [briefSaveError, setBriefSaveError] = useState('');
  const [confirmedMissingCoreContext, setConfirmedMissingCoreContext] = useState(false);
  const [draftTransferStatus, setDraftTransferStatus] = useState(null);

  useEffect(() => {
    if (!open) setBrief(makeDietBrief(patient));
  }, [open, patient]);

  useEffect(() => {
    if (!autoOpen) return;
    setOpen(true);
    if (initialInstructions) setInstructions(initialInstructions);
  }, [autoOpen, initialInstructions]);

  useEffect(() => {
    if (!open || protocols.length) return;
    let active = true;
    setProtocolsLoading(true);
    setProtocolError('');
    dietProtocolsApi.getAll()
      .then(items => { if (active) setProtocols(items); })
      .catch(requestError => { if (active) setProtocolError(requestError.message); })
      .finally(() => { if (active) setProtocolsLoading(false); });
    return () => { active = false; };
  }, [open, protocols.length]);

  const selectedProtocol = protocols.find(item => item.id === protocolVersionId);

  const effectiveLatestVisit = latestVisit || patient.latestVisit;
  const missingCoreContext = getMissingCoreContext(effectiveLatestVisit, latestMeasurement);
  const canUseAvailableCoreContext = !missingCoreContext.length || confirmedMissingCoreContext;
  const completeBriefItems = Object.values(brief).filter(value => String(value || '').trim()).length;
  const briefComplete = isDietBriefComplete(brief);
  const readiness = useMemo(() => [
    { label: 'Ultima visita', ready: Boolean(effectiveLatestVisit), value: effectiveLatestVisit ? `${effectiveLatestVisit.type} · ${new Date(effectiveLatestVisit.startsAt).toLocaleDateString('it-IT')}` : 'Assente' },
    { label: 'Ultima BIA', ready: Boolean(latestMeasurement), value: latestMeasurement ? new Date(latestMeasurement.measuredAt).toLocaleDateString('it-IT') : 'Assente' },
    { label: 'Obiettivi clinici', ready: Boolean(brief.clinicalGoals.trim()), value: brief.clinicalGoals.trim() ? 'Presenti' : 'Da integrare' },
    { label: 'Preferenze ed esclusioni', ready: Boolean(brief.foodPreferences.trim()), value: brief.foodPreferences.trim() ? 'Presenti' : 'Da integrare' },
    { label: 'Abitudini alimentari', ready: Boolean(brief.eatingHabits.trim()), value: brief.eatingHabits.trim() ? 'Presenti' : 'Da integrare' }
  ], [effectiveLatestVisit, latestMeasurement, brief]);

  const updateBrief = event => {
    setBrief(current => ({ ...current, [event.target.name]: event.target.value }));
    setBriefSaveError('');
    setError('');
  };

  const generate = async event => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setBriefSaveError('');
    setResult(null);
    setDraftTransferStatus(null);
    try {
      if (!briefComplete) throw new Error('Completa i tre dati guida prima di generare il piano');
      if (!canUseAvailableCoreContext) throw new Error('Conferma la verifica dei dati clinici mancanti prima di generare il piano');
      if (saveBriefToPatient) {
        try {
          const updatedPatient = await patientsApi.update(patient.id, {
            ...patient,
            clinicalGoals: brief.clinicalGoals.trim(),
            foodPreferences: brief.foodPreferences.trim(),
            eatingHabits: brief.eatingHabits.trim(),
            isActive: true
          });
          onPatientContextSaved?.(updatedPatient);
        } catch (saveError) {
          setBriefSaveError(`${saveError.message}. I dati sono rimasti nel briefing e non è stata avviata la generazione.`);
          return;
        }
      }
      const response = await dietPlansApi.generateDraft({
        patientId: patient.id,
        instructions: instructions.trim() || null,
        clinicalGoals: brief.clinicalGoals.trim(),
        foodPreferences: brief.foodPreferences.trim(),
        eatingHabits: brief.eatingHabits.trim(),
        planningMethod,
        protocolVersionId: protocolVersionId || null,
        clinicianConfirmedEligibility,
        targetCalories: planningMethod === 'EnergyTarget' && targetCalories ? Number(targetCalories) : null,
        mealsPerDay: Number(mealsPerDay),
        days: Number(days)
      });
      setResult(response);
      if (response.interpreter === 'OpenAI') {
        const transferResult = await onDraftReady?.(response);
        setDraftTransferStatus(getDraftTransferStatus(transferResult));
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  return <section className={`diet-ai-assistant${open ? ' is-open' : ''}`} aria-labelledby="dietAiTitle">
    <div className="diet-ai-intro">
      <div>
        <h2 id="dietAiTitle">Prepara una bozza con Sibilla</h2>
        <p>Usa il contesto disponibile in cartella, visita e BIA, dichiarando ciò che manca. Il risultato resta modificabile e non viene salvato automaticamente.</p>
      </div>
      <button type="button" className="clinic-button" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-controls="dietAiWorkspace">
        {open ? 'Chiudi assistente' : 'Prepara piano con AI'}
      </button>
    </div>

    {open && <form id="dietAiWorkspace" className="diet-ai-workspace" onSubmit={generate}>
      <div className="diet-ai-context" aria-label="Dati disponibili per la bozza">
        <h3>Contesto disponibile</h3>
        <dl>
          {readiness.map(item => <div key={item.label} className={item.ready ? 'ready' : 'missing'}>
            <dt>{item.label}</dt><dd>{item.value}</dd>
          </div>)}
        </dl>
        {latestMeasurement && <p>
          Peso {formatValue(latestMeasurement.weightKg, ' kg', 1)} · BMI {formatValue(latestMeasurement.bodyComposition?.bmi, '', 1)} · metabolismo basale {formatValue(latestMeasurement.bodyComposition?.bmr, ' kcal')}
        </p>}
      </div>

      <div className="diet-ai-brief clinic-form">
        <fieldset className="diet-guided-brief">
          <legend>
            <span>Definisci ciò che guida il piano</span>
            <strong className={briefComplete ? 'complete' : ''}>{completeBriefItems}/3 completi</strong>
          </legend>
          <p>Tre informazioni essenziali evitano una proposta generica. Puoi usarle solo adesso oppure conservarle nella cartella.</p>
          <label className={brief.clinicalGoals.trim() ? 'is-complete' : ''}>
            <span className="diet-brief-index" aria-hidden="true">1</span>
            <span><strong>Obiettivo concordato</strong><small>Risultato clinico e comportamentale atteso, con le priorità del percorso.</small></span>
            <textarea required name="clinicalGoals" value={brief.clinicalGoals} onChange={updateBrief} maxLength="4000" placeholder="Es. riduzione del peso preservando la massa muscolare e miglioramento dell’aderenza…" />
          </label>
          <label className={brief.foodPreferences.trim() ? 'is-complete' : ''}>
            <span className="diet-brief-index" aria-hidden="true">2</span>
            <span><strong>Allergie, esclusioni e preferenze</strong><small>Indica anche esplicitamente quando non risultano allergie o alimenti esclusi.</small></span>
            <textarea required name="foodPreferences" value={brief.foodPreferences} onChange={updateBrief} maxLength="4000" placeholder="Es. nessuna allergia nota; non gradisce…, preferisce…, alimenti da evitare…" />
          </label>
          <label className={brief.eatingHabits.trim() ? 'is-complete' : ''}>
            <span className="diet-brief-index" aria-hidden="true">3</span>
            <span><strong>Giornata alimentare reale</strong><small>Orari, pasti fuori casa, lavoro, attività fisica e difficoltà organizzative.</small></span>
            <textarea required name="eatingHabits" value={brief.eatingHabits} onChange={updateBrief} maxLength="4000" placeholder="Es. colazione alle 7:30, pranzo fuori casa dal lunedì al venerdì, allenamento…" />
          </label>
          <div className="diet-brief-persistence">
            <label><input type="checkbox" checked={saveBriefToPatient} onChange={event => setSaveBriefToPatient(event.target.checked)} />
              <span><strong>Salva anche nella cartella clinica</strong><small>Se non selezioni questa opzione, i dati saranno usati soltanto per la bozza corrente.</small></span>
            </label>
          </div>
          {briefSaveError && <div className="diet-brief-save-error" role="alert"><strong>Contesto non salvato.</strong> {briefSaveError}</div>}
        </fieldset>
        <label className="diet-protocol-field">Protocollo alimentare
          <select value={protocolVersionId} onChange={event => { setProtocolVersionId(event.target.value); setClinicianConfirmedEligibility(false); }}>
            <option value="">Personalizzazione generale, nessun protocollo specifico</option>
            {protocolsLoading && <option disabled>Caricamento protocolli…</option>}
            {protocols.map(protocol => <option key={protocol.id} value={protocol.id} disabled={protocol.reviewDue}>
              {protocol.name} · v{protocol.version}{protocol.reviewDue ? ' — revisione scaduta' : ''}
            </option>)}
          </select>
          <small>{selectedProtocol?.description || 'La scelta viene associata alla bozza con la sua versione, così resta tracciabile nel tempo.'}</small>
          {protocolError && <small className="diet-protocol-error" role="alert">Catalogo non disponibile: {protocolError}. Riprova chiudendo e riaprendo l’assistente.</small>}
        </label>
        {selectedProtocol?.requiresEligibilityConfirmation && <div className="diet-protocol-safety">
          <strong>Protocollo ad alta sorveglianza clinica</strong>
          <p>Prima della generazione verifica:</p>
          <ul>{selectedProtocol.requiredChecks.map(check => <li key={check}>{check}</li>)}</ul>
          <label>
            <input type="checkbox" checked={clinicianConfirmedEligibility} onChange={event => setClinicianConfirmedEligibility(event.target.checked)} />
            Ho verificato indicazione, controindicazioni, farmaci/esami e piano di monitoraggio.
          </label>
        </div>}
        <fieldset className="diet-method-choice">
          <legend>Metodo di elaborazione</legend>
          <label className={planningMethod === 'PortionsAndFrequencies' ? 'selected' : ''}>
            <input type="radio" name="planningMethod" value="PortionsAndFrequencies" checked={planningMethod === 'PortionsAndFrequencies'} onChange={event => setPlanningMethod(event.target.value)} />
            <span><strong>Porzioni e frequenze</strong><small>Senza conteggio calorico; usa alternative e misure pratiche.</small></span>
          </label>
          <label className={planningMethod === 'EnergyTarget' ? 'selected' : ''}>
            <input type="radio" name="planningMethod" value="EnergyTarget" checked={planningMethod === 'EnergyTarget'} onChange={event => setPlanningMethod(event.target.value)} />
            <span><strong>Obiettivo energetico</strong><small>Usa le kcal definite dalla nutrizionista.</small></span>
          </label>
        </fieldset>
        <label>Indicazioni della nutrizionista
          <textarea value={instructions} onChange={event => setInstructions(event.target.value)} maxLength="4000" placeholder="Es. obiettivo concordato, alimenti da escludere, orari di lavoro, attività fisica e numero di alternative desiderate…" />
        </label>
        <div className="diet-ai-controls">
          {planningMethod === 'EnergyTarget' && <label>Obiettivo energetico (kcal)
            <input type="number" min="500" max="10000" required value={targetCalories} onChange={event => setTargetCalories(event.target.value)} placeholder="Definito dal professionista" />
          </label>}
          <label>Pasti giornalieri
            <select value={mealsPerDay} onChange={event => setMealsPerDay(event.target.value)}>
              {[3, 4, 5, 6, 7, 8].map(value => <option key={value} value={value}>{value}</option>)}
            </select>
          </label>
          <label>Durata del piano
            <select value={days} onChange={event => setDays(event.target.value)}>
              {[1, 2, 3, 5, 7, 10, 14].map(value => <option key={value} value={value}>{value} {value === 1 ? 'giorno' : 'giorni'}</option>)}
            </select>
          </label>
        </div>
        <div className="diet-ai-submit">
          <div className="diet-ai-submit-status">
            {!!missingCoreContext.length && <div className="diet-core-context-warning">
              <strong>Dati non disponibili: {missingCoreContext.join(' e ')}.</strong>
              <span>La bozza userà soltanto le informazioni presenti e il briefing appena compilato.</span>
              <label><input type="checkbox" checked={confirmedMissingCoreContext} onChange={event => setConfirmedMissingCoreContext(event.target.checked)} /> Ho verificato l’assenza di questi dati e scelgo di procedere.</label>
            </div>}
            <p aria-live="polite">{briefComplete
              ? (canUseAvailableCoreContext ? 'Il briefing è completo. Sibilla preparerà una bozza da verificare clinicamente.' : 'Briefing completo. Conferma la verifica dei dati clinici mancanti.')
              : `Completa ancora ${3 - completeBriefItems} ${3 - completeBriefItems === 1 ? 'dato guida' : 'dati guida'} per abilitare la generazione.`}</p>
          </div>
          <button type="submit" className={`clinic-button${loading ? ' is-loading' : ''}`} disabled={loading || !briefComplete || !canUseAvailableCoreContext || Boolean(selectedProtocol?.requiresEligibilityConfirmation && !clinicianConfirmedEligibility)}>{loading ? (saveBriefToPatient ? 'Salvataggio e generazione…' : 'Elaborazione in corso…') : 'Genera e apri nell’editor'}</button>
        </div>
      </div>
    </form>}

    {error && <div className="diet-ai-feedback error" role="alert"><strong>Bozza non preparata</strong><span>{error}. Puoi correggere le indicazioni e riprovare.</span></div>}
    {result && <div className={`diet-ai-feedback${result.interpreter === 'OpenAI' ? '' : ' error'}`} role="status">
      <strong>{result.interpreter !== 'OpenAI'
        ? 'Bozza IA non generata'
        : draftTransferStatus === 'not-replaced' ? 'Bozza pronta, editor non sostituito' : 'Bozza IA pronta e aperta nell’editor'}</strong>
      <span>{result.interpreter === 'OpenAI' ? 'Elaborata con IA' : 'È disponibile soltanto uno schema locale vuoto'} · {result.draft.planningMethod === 'EnergyTarget' ? 'con obiettivo energetico' : 'per porzioni e frequenze, senza calorie'} · non è stata salvata.</span>
      {result.protocol && <p><b>Protocollo:</b> {result.protocol.name}, versione {result.protocol.version}.</p>}
      {result.dataUsed?.visitStartsAt && <p><b>Visita usata:</b> {result.dataUsed.visitType || 'Visita'} del {new Date(result.dataUsed.visitStartsAt).toLocaleDateString('it-IT')}.</p>}
      {!!result.missingContext?.length && <p><b>Da integrare:</b> {result.missingContext.join(', ')}.</p>}
      {!!result.warnings?.length && <ul>{result.warnings.map(warning => <li key={warning}>{warning}</li>)}</ul>}
      <button type="button" className="clinic-button" onClick={async () => setDraftTransferStatus(getDraftTransferStatus(await onDraftReady?.(result)))}>{result.interpreter === 'OpenAI' ? (draftTransferStatus === 'not-replaced' ? 'Apri la bozza nell’editor' : 'Riapri nell’editor') : 'Apri lo schema vuoto manuale'}</button>
    </div>}
  </section>;
}

export default AiDietPlanAssistant;
